import { useCallback, useEffect, useState } from 'react';
import { useTasksListQuery } from 'api';
import { useNavigate } from 'react-router-dom';
import { dateFormatter } from 'constants/index';
import { ADMIN_ROUTES } from 'constants/routes';
import { TaskDataType } from 'components/common/tasks';
import { TablePaginationConfig, TableProps } from 'components';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { OrderType, PaginationType, SortParamsType } from 'typings';
import { AllEntityTypes } from 'models/Task';

export interface IFilterParams {
  department?: string;
  title?: string;
  roleId?: string;
  countryId?: string;
  languages?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  lastModifiedDateFrom?: string;
  lastModifiedDateTo?: string;
}

const initialPagination: PaginationType = { page: 1, size: 10 };
const initialSort: SortParamsType = {
  field: null,
  order: null,
};

interface UseTasksProps {
  entityType?: string;
  entityId?: string;
  isMainTable?: boolean;
  forDashboard?: boolean;
}

export const useTasks = ({ entityType, entityId, isMainTable, forDashboard }: UseTasksProps = {}) => {
  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState<IFilterParams>({});
  const [pagination, setPagination] = useState<PaginationType>(initialPagination);
  const [sort, setSort] = useState<SortParamsType>(forDashboard ? { field: 'status', order: 'asc' } : initialSort);
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [deleteItem, setDeleteItem] = useState<Record<string, any> | undefined>();

  const navigate = useNavigate();
  const {
    data: { content = [], totalElements = 0 } = {},
    isLoading,
    refetch,
  } = useTasksListQuery({
    size: pagination.size,
    page: pagination.page,
    sortField: forDashboard && !sort.field ? 'status' : sort.field,
    sortOrder: forDashboard && !sort.field ? 'asc' : sort.order,
    search,
    filters,
    entityType,
    entityId,
    isMainTable,
    forDashboard,
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const onOpenFilters = useCallback(() => setOpenFilters(true), []);
  const onCloseFilters = useCallback(() => setOpenFilters(false), []);

  const routeChange = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.tasks.view}/${value}/general`);
    },
    [navigate]
  );
  const onEntityClick = useCallback(
    (type: string, id: string) => {
      let path = `${AllEntityTypes[type].path}/${id}`;
      if (AllEntityTypes[type].tab) {
        path = `${path}/${AllEntityTypes[type].tab}`;
      }
      navigate(path);
    },
    [navigate]
  );

  const onAdd = useCallback(() => {
    navigate(ADMIN_ROUTES.tasks.create);
  }, [navigate]);

  const handleTableChange: TableProps<TaskDataType>['onChange'] = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<TaskDataType> | SorterResult<TaskDataType>[]
    ) => {
      const sortField = Array.isArray(sorter) ? sorter[0].columnKey : sorter.columnKey;
      const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
      const order = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
      setPagination({
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      });
      setSort({
        field: sortField as string,
        order: order as OrderType,
      });
    },
    []
  );

  const onDeleteModalStateChange = useCallback((value: any | undefined) => {
    setDeleteItem(value);
  }, []);

  const onSearch = useCallback((value: string) => {
    setPagination((oState) => ({
      ...oState,
      page: 1,
    }));
    setSearch(value);
  }, []);

  const applyFilters = useCallback(
    (values: Record<string, any>) => {
      const _filters: any = {
        ...(values.assignedTo && { assignedTo: values.assignedTo }),
        ...(values.entityType && { entityType: values.entityType }),
        ...(values.createdBy && { createdBy: values.createdBy }),
        ...(values.priorityLevel?.length && { priorityLevel: values.priorityLevel }),
        ...(values.status?.length && { status: values.status }),

        ...(values.createdDate?.[0] && {
          createdDateFrom: dateFormatter(values.createdDate[0], 'start'),
        }),
        ...(values.createdDate?.[1] && {
          createdDateTo: dateFormatter(values.createdDate[1], 'end'),
        }),
        ...(values.lastUpdatedDate?.[0] && {
          lastUpdatedDateFrom: dateFormatter(values.lastUpdatedDate[0], 'start'),
        }),
        ...(values.lastUpdatedDate?.[1] && {
          lastUpdatedDateTo: dateFormatter(values.lastUpdatedDate[1], 'end'),
        }),
        ...(values.dueDate?.[0] && {
          dueDateFrom: dateFormatter(values.dueDate[0], 'start'),
        }),
        ...(values.dueDate?.[1] && {
          dueDateTo: dateFormatter(values.dueDate[1], 'end'),
        }),
      };
      setFilters(_filters);
      setPagination((oState) => ({
        ...oState,
        page: 1,
      }));
      onCloseFilters();
    },
    [onCloseFilters]
  );
  const resetFilters = useCallback(() => setFilters({}), []);

  return {
    onOpenFilters,
    onCloseFilters,
    routeChange,
    data: content,
    total: totalElements ?? 0,
    handleTableChange,
    openFilters,
    applyFilters,
    pagination,
    isLoading,
    onSearch,
    onAdd,
    onEntityClick,
    onDeleteModalStateChange,
    deleteItem,
    resetFilters,
  };
};
